import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '../ui';
import type { ValidationResult } from '../../utils';

interface ValidationWarningBannerProps {
  validation: ValidationResult | null;
  onDismiss?: () => void;
  className?: string;
}

export const ValidationWarningBanner: React.FC<
  ValidationWarningBannerProps
> = ({ validation, onDismiss, className = '' }) => {
  const { t } = useTranslation();

  if (!validation || validation.isValid) {
    return null;
  }

  const { summary, warningCount, errorCount } = validation;
  const issues: string[] = [];

  if (summary.emptyRows > 0) {
    issues.push(
      t('listParser.validation.emptyRows', { count: summary.emptyRows })
    );
  }

  if (summary.rowsWithMissingData > 0) {
    issues.push(
      t('listParser.validation.missingData', {
        count: summary.rowsWithMissingData,
      })
    );
  }

  if (summary.rowsWithInvalidQuantity > 0) {
    issues.push(
      t('listParser.validation.invalidQuantity', {
        count: summary.rowsWithInvalidQuantity,
      })
    );
  }

  if (summary.rowsWithInvalidFormat > 0) {
    issues.push(
      t('listParser.validation.invalidFormat', {
        count: summary.rowsWithInvalidFormat,
      })
    );
  }

  return (
    <Alert variant="warning" className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="flex-1">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium">
              {t('listParser.validation.issuesFound', {
                warnings: warningCount,
                errors: errorCount,
              })}
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              {t('listParser.validation.totalRowsScanned', {
                total: summary.totalRows,
                valid: summary.validRows,
              })}
            </p>
            <div className="mt-2 space-y-1">
              {issues.map((issue, index) => (
                <p key={index} className="text-sm">
                  • {issue}
                </p>
              ))}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('listParser.validation.canProceed')}
            </p>
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="ml-4 flex-shrink-0 p-1 hover:bg-background rounded"
              aria-label={t('tools.errorBanner.dismiss')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};
